import { endpoints } from '@/config/api';
import { apiClient } from '@/lib/apiClient';
import { settingsService } from '@/services/settingsService';
import type { Trip } from '@/types';
import { TripType } from '@/types';

const THRESHOLD_KM = 20_000;

export interface RateResult {
  ratePerKm: number;
  totalAmount: number;
}

export interface RecalculateYearSummary {
  year: number;
  businessTrips: number;
  unresolvedTrips: number;
  thresholdTotal: number;
  flatTotal: number;
  difference: number;
}

/**
 * Pure threshold rate computation — no side effects, no network calls.
 *
 * @param kmSoFar    Business km already driven in the fiscal year before this trip.
 * @param distanceKm Distance of this trip in kilometres.
 * @param rateUnder  Rate per km for the first 20 000 km.
 * @param rateOver   Rate per km above 20 000 km.
 */
export function computeRate(
  kmSoFar: number,
  distanceKm: number,
  rateUnder: number,
  rateOver: number,
): RateResult {
  if (distanceKm <= 0) return { ratePerKm: rateUnder, totalAmount: 0 };

  if (kmSoFar >= THRESHOLD_KM) {
    return { ratePerKm: rateOver, totalAmount: parseFloat((distanceKm * rateOver).toFixed(2)) };
  }

  if (kmSoFar + distanceKm <= THRESHOLD_KM) {
    return { ratePerKm: rateUnder, totalAmount: parseFloat((distanceKm * rateUnder).toFixed(2)) };
  }

  // Trip crosses the 20 000 km boundary — blended rate
  const kmUnder = THRESHOLD_KM - kmSoFar;
  const kmOver = distanceKm - kmUnder;
  const totalAmount = parseFloat((kmUnder * rateUnder + kmOver * rateOver).toFixed(2));
  const blendedRate = parseFloat((totalAmount / distanceKm).toFixed(4));
  return { ratePerKm: blendedRate, totalAmount };
}

/** Fetch all trips for the current user from the backend (un-paginated). */
async function getAllTrips(): Promise<Trip[]> {
  return apiClient.get<Trip[]>(endpoints.trips.list);
}

/**
 * Calculate the threshold-aware rate for a single trip.
 *
 * Fetches fiscal year rates and the cumulative business km for the given year,
 * then delegates to `computeRate` for the actual calculation.
 *
 * @param year           Calendar year of the trip (e.g. 2026).
 * @param distanceKm     Distance of the trip in kilometres.
 * @param excludeTripId  When editing an existing trip, exclude its own distance
 *                       from the km-so-far sum to avoid double-counting.
 * @param fallbackRates  Rates to use when no fiscal year is found for the year.
 */
export async function calculateRateForTrip(
  year: number,
  distanceKm: number,
  excludeTripId?: string,
  fallbackRates = { rateUnder: 3.56, rateOver: 2.0 },
): Promise<RateResult & { rateUnder: number; rateOver: number }> {
  const [fiscalYears, allTrips] = await Promise.all([
    settingsService.getFiscalYears(),
    getAllTrips(),
  ]);

  const kmSoFar = allTrips
    .filter(
      (t) =>
        t.type === TripType.Business &&
        new Date(t.date).getFullYear() === year &&
        t.id !== excludeTripId,
    )
    .reduce((sum, t) => sum + Number(t.distance), 0);

  const fy = fiscalYears.find((y) => y.year === year);
  const rateUnder = fy?.rateUnder20000 ?? fallbackRates.rateUnder;
  const rateOver = fy?.rateOver20000 ?? fallbackRates.rateOver;

  return { ...computeRate(kmSoFar, distanceKm, rateUnder, rateOver), rateUnder, rateOver };
}

/**
 * Recalculate threshold-aware RatePerKm and TotalAmount for all business trips in a year.
 *
 * Calls POST /api/trips/recalculate/{year} on the Azure backend, which:
 *  1. Fetches all business trips for the year in chronological order.
 *  2. Applies the 20,000 km threshold split to each trip.
 *  3. Persists RatePerKm and TotalAmount on every Trip row.
 *  4. Returns a summary of the recalculation.
 *
 * Falls back to client-side computation (read-only, no persistence) if the
 * backend call fails — e.g. during local development without the function running.
 */
export async function recalculateYearTrips(
  year: number,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _fallbackRates = { rateUnder: 3.56, rateOver: 2.0 },
): Promise<RecalculateYearSummary> {
  const result = await apiClient.post<RecalculateYearSummary>(
    endpoints.trips.recalculate(year),
  );
  return result;
}
